import React from 'react';
import { HiOutlineCheck, HiOutlineXMark } from 'react-icons/hi2';

const INCLUDED_FEATURES = [
  { text: 'Scheduling' },
  { text: 'Page Unblocker', badge: 'AI', tooltip: 'Dismiss popups, solve captchas, and more with our AI-powered Page Unblocker.' },
  { text: 'Data Labeling', badge: 'AI', tooltip: 'Data columns are automatically labeled at the end of every automation.' },
  { text: 'Proxies', tooltip: 'We use ISP proxies. Residential proxies coming soon.' },
  { text: 'Integrations', subtitle: 'Webhooks, Google Sheets', tooltip: 'Send extracted data wherever you need it. More integrations coming soon.' },
];

// UI copy keyed by plan name. New plans from the API render with empty defaults
// until copy is added here — they never break the grid.
const PLAN_COPY = {
  light: {
    tagline: 'Unlock the full extension',
    badge: null,
    features: [
      { text: 'Browser Extension', included: true, highlight: true, badge: 'PRO', tooltip: 'Unlock all PRO features of the Browser Extension — unlimited exports, image downloads, multi-tab extraction, and more.' },
      { text: '3,000 cloud credits/month', subtitle: 'For the occasional cloud run', included: true, tooltip: '1 credit per page. Use the cloud platform when you need scheduling, proxies, or background extraction.' },
      { text: '1 Cloud Browser', included: true, tooltip: 'Run 1 cloud automation at a time. Upgrade to Starter for more capacity.' },
    ],
  },
  starter: {
    tagline: 'For simple scraping needs',
    badge: null,
    features: [
      { text: 'Browser Extension', included: true, badge: 'PRO', tooltip: 'Unlock all PRO features of the Browser Extension — included for every team member.' },
      { text: '15,000 credits/month', included: true, highlight: true, tooltip: '1 credit per page. Features like Page Unblocker, Proxies, and Data Labeling also cost credits.' },
      { text: '2 Cloud Browsers', subtitle: 'Run 2 tasks simultaneously', included: true, tooltip: 'Run automations in cloud browsers. More browsers let you run more automations simultaneously.' },
    ],
  },
  pro: {
    tagline: 'For demanding individuals',
    badge: 'Most Popular',
    features: [
      { text: 'Browser Extension', included: true, badge: 'PRO', tooltip: 'Unlock all PRO features of the Browser Extension — included for every team member.' },
      { text: '75,000 credits/month', included: true, highlight: true, tooltip: '1 credit per page. Features like Page Unblocker, Proxies, and Data Labeling also cost credits.' },
      { text: '3 Cloud Browsers', subtitle: 'Run 3 tasks simultaneously', included: true, tooltip: 'Run automations in cloud browsers. More browsers let you run more automations simultaneously.' },
      { text: 'Team Access (3 seats)', included: true, tooltip: [
        'Invite up to 3 teammates to a shared workspace',
        'Every team member gets full PRO Browser Extension',
        'Access to a shared workspace cloud dashboard',
      ] },
    ],
  },
  max: {
    tagline: 'For teams',
    badge: null,
    features: [
      { text: 'Browser Extension', included: true, badge: 'PRO', tooltip: 'Unlock all PRO features of the Browser Extension — included for every team member.' },
      { text: '200,000 credits/month', included: true, highlight: true, tooltip: '1 credit per page. Features like Page Unblocker, Proxies, and Data Labeling also cost credits.' },
      { text: '5 Cloud Browsers', subtitle: 'Run 5 tasks simultaneously', included: true, tooltip: 'Run automations in cloud browsers. More browsers let you run more automations simultaneously.' },
      { text: 'Team Access (10 seats)', included: true, highlight: true, tooltip: [
        'Invite up to 10 teammates to a shared workspace',
        'Every team member gets full PRO Browser Extension',
        'Access to a shared workspace cloud dashboard',
      ] },
    ],
  },
};

const EMPTY_COPY = { tagline: '', badge: null, features: [] };

const FeatureTooltip = ({ tooltip, side = 'right' }) => {
  const isList = Array.isArray(tooltip);
  const isLeft = side === 'left';
  return (
    <span className={`pointer-events-none absolute top-1/2 -translate-y-1/2 w-52 opacity-0 scale-95 group-hover/tip:opacity-100 group-hover/tip:scale-100 transition-all duration-200 z-50 ${isLeft ? 'right-full mr-3' : 'left-full ml-3'}`}>
      <span className={`block px-3.5 py-2.5 text-xs font-normal text-gray-100 bg-gray-900/95 dark:bg-gray-800/95 backdrop-blur-sm rounded-xl shadow-xl shadow-cyan-500/10 dark:shadow-cyan-500/5 border border-cyan-500/20 dark:border-cyan-400/15 leading-relaxed whitespace-normal ${isList ? 'text-left' : 'text-center'}`}>
        {isList ? (
          <ul className="space-y-1.5">
            {tooltip.map((point, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-gray-400 flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        ) : (
          tooltip
        )}
      </span>
      <span className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-gray-900/95 dark:bg-gray-800/95 rotate-45 ${isLeft ? '-right-1.5 border-t border-r border-cyan-500/20 dark:border-cyan-400/15' : '-left-1.5 border-b border-l border-cyan-500/20 dark:border-cyan-400/15'}`} />
    </span>
  );
};

const PlanCard = ({ plan, currentPlan, isAnnual, getPlanAction, actionLoading }) => {
  const config = PLAN_COPY[plan.name] || EMPTY_COPY;

  const { label, disabled, action, tooltip } = getPlanAction(plan);
  const isCurrent = currentPlan === plan.name;
  const isFeatured = plan.name === 'pro';
  const tooltipSide = plan.name === 'max' ? 'left' : 'right';

  return (
    <div className={`relative rounded-2xl px-6 pt-9 pb-8 transition-all duration-500 ${
      isFeatured
        ? 'border-2 border-cyan-400/50 dark:border-cyan-400/40 bg-gradient-to-br from-cyan-400/[0.08] via-blue-500/[0.04] to-purple-500/[0.04] dark:from-cyan-400/[0.06] dark:via-blue-500/[0.03] dark:to-gray-900/80 shadow-glow-cyan md:scale-[1.03] z-10 hover:shadow-glow-md'
        : 'border border-gray-200 dark:border-gray-700/30 bg-gradient-to-br from-gray-50 dark:from-gray-900/60 to-gray-100 dark:to-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600/50 hover:shadow-md'
    }`}>
      {/* Subtle ambient glow for Pro */}
      {isFeatured && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-[0.06] blur-xl pointer-events-none" />
      )}

      {/* Badge */}
      {config.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20">
          <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full shadow-lg ${
            isFeatured
              ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white shadow-glow-cyan'
              : 'bg-gradient-to-r from-gray-600 to-gray-700 text-white border border-gray-500/50'
          }`}>
            {config.badge}
          </span>
        </div>
      )}

      {/* Header */}
      <div className={`relative ${config.badge ? 'mt-1' : ''}`}>
        <h4 className={`text-sm font-semibold uppercase tracking-widest ${
          isFeatured
            ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
            : 'text-gray-800 dark:text-gray-100'
        }`}>{plan.display_name}</h4>
        <p className="text-[13px] font-medium text-gray-500 dark:text-gray-400 mt-1">{config.tagline}</p>
      </div>

      {/* Pricing */}
      <div className="relative mt-5 mb-6">
        {isAnnual ? (
          <div>
            <div className="flex items-baseline gap-2 mb-1">
              <span className="text-sm text-gray-400 dark:text-gray-500 line-through font-medium decoration-gray-400/70 dark:decoration-gray-500/70">
                ${plan.price}
              </span>
              <span className={`text-5xl font-extrabold tracking-tight ${
                isFeatured
                  ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                ${plan.yearly_price}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500 font-normal">/mo</span>
            </div>

            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
              Billed ${plan.yearly_price * 12} per year
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-baseline gap-2">
              <span className={`text-5xl font-extrabold tracking-tight ${
                isFeatured
                  ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
                  : 'text-gray-900 dark:text-gray-100'
              }`}>
                ${plan.price}
              </span>
              <span className="text-sm text-gray-400 dark:text-gray-500 font-normal">/mo</span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">Billed monthly</p>
          </div>
        )}
      </div>

      {/* CTA — directly below price for strong price→action association */}
      <div className="relative mb-7">
        {disabled ? (
          <div
            title={tooltip || undefined}
            className={`relative z-10 w-full py-3 rounded-xl font-semibold text-sm text-center transition-all ${
              isFeatured
                ? 'text-cyan-600 dark:text-cyan-400 bg-cyan-500/10 border border-cyan-400/20 dark:border-cyan-400/15'
                : 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700/30'
            } ${tooltip ? 'cursor-help' : 'cursor-default'}`}
          >
            {label}
          </div>
        ) : (
          <button
            onClick={action}
            disabled={actionLoading === plan.name}
            className={`relative z-10 w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-300 transform disabled:opacity-50 group cursor-pointer ${
              isFeatured
                ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-glow-md hover:scale-[1.03] border-0'
                : 'bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 border-0 hover:scale-[1.03] hover:shadow-md'
            }`}
          >
            {actionLoading === plan.name ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Processing...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                {label}
                <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
              </span>
            )}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className={`mb-6 ${
        isFeatured
          ? 'h-px bg-gradient-to-r from-transparent via-cyan-400/20 to-transparent'
          : 'border-t border-gray-200/40 dark:border-gray-700/30'
      }`} />

      {/* Features */}
      <ul className="relative space-y-3 mb-7">
        {config.features.map((feature, index) => (
          <li key={index} className="flex items-start gap-2.5">
            {feature.included ? (
              <HiOutlineCheck className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                feature.highlight
                  ? 'text-cyan-500 dark:text-cyan-400'
                  : 'text-emerald-500'
              }`} />
            ) : (
              <HiOutlineXMark className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-300 dark:text-gray-700" />
            )}
            <div className={`text-sm leading-tight ${
              feature.included
                ? feature.subtle
                  ? 'text-xs text-gray-500 dark:text-gray-400 italic'
                  : feature.highlight
                    ? isFeatured
                      ? 'text-gray-800 dark:text-gray-100 font-semibold'
                      : 'text-gray-700 dark:text-gray-200 font-semibold'
                    : 'text-gray-600 dark:text-gray-300'
                : 'text-gray-300 dark:text-gray-600'
            }`}>
              <span className={`relative group/tip flex items-center gap-1.5 ${feature.tooltip ? 'cursor-default border-b border-dashed border-gray-400/40' : ''}`}>
                {feature.text}
                {feature.badge && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white leading-none">
                    {feature.badge}
                  </span>
                )}
                {feature.tooltip && (
                  <FeatureTooltip tooltip={feature.tooltip} side={tooltipSide} />
                )}
              </span>
              {feature.subtitle && (
                <span className="block text-xs text-gray-400 dark:text-gray-500 font-normal mt-0.5">
                  {feature.subtitle}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Also included */}
      <div className="relative">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">Also included</p>
        <ul className="space-y-2.5">
          {INCLUDED_FEATURES.map((feat, index) => (
            <li key={index} className="flex items-start gap-2.5">
              <HiOutlineCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <span className={`relative group/tip flex items-center gap-1.5 ${feat.tooltip ? 'cursor-default border-b border-dashed border-gray-400/40' : ''}`}>
                  {feat.text}
                  {feat.badge && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white leading-none">
                      {feat.badge}
                    </span>
                  )}
                  {feat.tooltip && (
                    <FeatureTooltip tooltip={feat.tooltip} side={tooltipSide} />
                  )}
                </span>
                {feat.subtitle && (
                  <span className="block text-xs text-gray-400 dark:text-gray-500 font-normal mt-0.5">
                    {feat.subtitle}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

    </div>
  );
};

export { INCLUDED_FEATURES };
export default PlanCard;
